import React from 'react';
import { Modal, Table, Typography, Divider, Row, Col, Button, Space } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const JournalVoucherModal = ({ visible, onCancel, data, type = 'Purchase' }) => {
    if (!data) return null;

    const journalEntry = data.journalEntry || data.journal_entry;

    const handlePrint = () => {
        const printContent = document.getElementById('journal-voucher-print-area').innerHTML;
        const originalContent = document.body.innerHTML;
        document.body.innerHTML = printContent;
        window.print();
        document.body.innerHTML = originalContent;
        window.location.reload(); // Reload to restore React state
    };

    const columns = [
        {
            title: 'Account Code',
            dataIndex: ['chart_of_account', 'code'],
            key: 'code',
            width: '150px',
        },
        {
            title: 'Account Name',
            dataIndex: ['chart_of_account', 'name'],
            key: 'name',
        },
        {
            title: 'Debit',
            dataIndex: 'debit',
            key: 'debit',
            align: 'right',
            render: (val) => val > 0 ? Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-',
            width: '150px',
        },
        {
            title: 'Credit',
            dataIndex: 'credit',
            key: 'credit',
            align: 'right',
            render: (val) => val > 0 ? Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-',
            width: '150px',
        }
    ];

    return (
        <Modal
            open={visible}
            onCancel={onCancel}
            width={850}
            footer={[
                <Button key="back" onClick={onCancel}>
                    Close
                </Button>,
                <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
                    Print Voucher
                </Button>,
            ]}
            title="Journal Voucher"
        >
            <div id="journal-voucher-print-area" style={{ padding: '20px', backgroundColor: '#fff', color: '#000' }}>
                <div style={{ textAlign: 'center', marginBottom: 30 }}>
                    <Title level={3} style={{ margin: 0, textTransform: 'uppercase' }}>BMPC Agri-Inventory System</Title>
                    <Text type="secondary">Journal Voucher</Text>
                </div>

                <Row gutter={[24, 12]} style={{ marginBottom: 24 }}>
                    <Col span={12}>
                        <Space direction="vertical" size={0}>
                            <Text strong type="secondary" style={{ fontSize: 10, textTransform: 'uppercase' }}>Voucher For</Text>
                            <Text strong style={{ fontSize: 16 }}>{data.partner?.name || 'N/A'}</Text>
                        </Space>
                    </Col>
                    <Col span={12} style={{ textAlign: 'right' }}>
                        <Space direction="vertical" size={0}>
                            <Text strong type="secondary" style={{ fontSize: 10, textTransform: 'uppercase' }}>Voucher No.</Text>
                            <Text strong style={{ fontSize: 16 }}>JV-{data.ticket_number || data.id}</Text>
                        </Space>
                    </Col>
                    <Col span={12}>
                        <Space direction="vertical" size={0}>
                            <Text strong type="secondary" style={{ fontSize: 10, textTransform: 'uppercase' }}>Date</Text>
                            <Text>{dayjs(data.transaction_date).format('MMMM DD, YYYY')}</Text>
                        </Space>
                    </Col>
                    <Col span={12} style={{ textAlign: 'right' }}>
                        <Space direction="vertical" size={0}>
                            <Text strong type="secondary" style={{ fontSize: 10, textTransform: 'uppercase' }}>Reference</Text>
                            <Text>{data.ticket_number}</Text>
                        </Space>
                    </Col>
                </Row>

                <Divider style={{ margin: '12px 0' }} />

                <div style={{ marginBottom: 24 }}>
                    <Text strong type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>Particulars</Text>
                    <Text>{data.remarks || `${type} of items recorded under ticket #${data.ticket_number}`}</Text>
                </div>

                <Table
                    dataSource={journalEntry?.items || []}
                    columns={columns}
                    pagination={false}
                    rowKey="id"
                    bordered
                    size="small"
                    summary={(pageData) => {
                        let totalDebit = 0;
                        let totalCredit = 0;
                        pageData.forEach(({ debit, credit }) => {
                            totalDebit += Number(debit);
                            totalCredit += Number(credit);
                        });

                        return (
                            <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 'bold' }}>
                                <Table.Summary.Cell index={0} colSpan={2} align="right">Total</Table.Summary.Cell>
                                <Table.Summary.Cell index={1} align="right">
                                    ₱ {totalDebit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={2} align="right">
                                    ₱ {totalCredit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        );
                    }}
                />

                <Row gutter={48} style={{ marginTop: 60, textAlign: 'center' }}>
                    <Col span={8}>
                        <div style={{ borderTop: '1px solid #000', paddingTop: 8 }}>
                            <Text strong style={{ display: 'block' }}>Prepared By</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>{data.creator?.name || 'Encoder'}</Text>
                        </div>
                    </Col>
                    <Col span={8}>
                        <div style={{ borderTop: '1px solid #000', paddingTop: 8 }}>
                            <Text strong style={{ display: 'block' }}>Reviewed By</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>Manager / Accountant</Text>
                        </div>
                    </Col>
                    <Col span={8}>
                        <div style={{ borderTop: '1px solid #000', paddingTop: 8 }}>
                            <Text strong style={{ display: 'block' }}>Approved By</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>Authorized Signatory</Text>
                        </div>
                    </Col>
                </Row>

                <style>
                    {`
                        @media print {
                            .ant-modal-close, .ant-modal-footer, .no-print {
                                display: none !important;
                            }
                            body {
                                padding: 0 !important;
                                margin: 0 !important;
                            }
                            #journal-voucher-print-area {
                                width: 100% !important;
                                padding: 40px !important;
                            }
                        }
                    `}
                </style>
            </div>
        </Modal>
    );
};

export default JournalVoucherModal;


